<template>
  <UiCard v-if="canUse || items.length" class="ra" :title="t('calendar.record.title')" flush>
    <template #actions>
      <UiButton v-if="canUse" size="sm" variant="secondary" @click="create">+ {{ t('calendar.record.new') }}</UiButton>
    </template>

    <div v-if="loading" class="ra__pad" aria-busy="true"><UiSkeleton :lines="2" /></div>
    <p v-else-if="failed" class="ra__pad ra__soft">{{ t('calendar.failed') }}</p>
    <p v-else-if="!items.length" class="ra__pad ra__soft">{{ t('calendar.record.none') }}</p>
    <ul v-else class="ra__list">
      <li v-for="item in items" :key="item.id">
        <RouterLink :to="item.route" class="ra__row" :class="{ 'ra__row--past': item.group === 'calendar.related.past' }">
          <span class="ra__title">{{ item.title }}</span>
          <span class="ra__meta">{{ item.subtitle }}</span>
        </RouterLink>
      </li>
    </ul>
    <RouterLink v-if="total > items.length && route" :to="route" class="ra__more">{{ t('calendar.record.all', { n: total }) }}</RouterLink>
  </UiCard>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiButton, UiCard, UiSkeleton, api } from '@general/ui-kit'
import { useAuthStore } from '@general/auth/frontend'

import { useCalendarEditor, changes } from '../store'

/**
 * The events linked to one record, on that record's own page — for the pages
 * with no «related» tabs (a supplier, an order, a project, a quote). Where the
 * page has tabs the same list is the platform's «Agenda» tab and this is not
 * needed. «New event» opens the editor already linked to this record.
 *
 * It asks the platform's related panel and nothing else: the same answer, with
 * the same rules about who sees which event, as the tab.
 */
const props = defineProps({
  type: { type: String, required: true },
  referenceId: { type: String, required: true },
  /** What the record is called, for the link the new event carries. */
  label: { type: String, default: '' },
})
const { t } = useI18n()
const auth = useAuthStore()
const { open } = useCalendarEditor()

const items = ref([])
const total = ref(0)
const route = ref('')
const loading = ref(true)
const failed = ref(false)
const canUse = computed(() => auth.can?.('calendar:use') ?? false)

async function load() {
  if (!props.referenceId) return
  loading.value = true
  failed.value = false
  try {
    const data = await api.get(`/related/${encodeURIComponent(props.type)}/${encodeURIComponent(props.referenceId)}/platform:calendar`, { params: { limit: 5 } })
    items.value = data.items ?? []
    total.value = data.total ?? items.value.length
    route.value = data.route ?? ''
  } catch {
    failed.value = true
  } finally {
    loading.value = false
  }
}

async function create() {
  const saved = await open({ links: [{ type: props.type, referenceId: props.referenceId, label: props.label }] })
  if (saved) load()
}

onMounted(load)
watch(() => [props.type, props.referenceId], load)
watch(changes, load)
</script>

<style scoped>
.ra { margin-top: var(--space-4); }
.ra__pad { margin: 0; padding: var(--space-3) var(--space-4); }
.ra__soft { color: var(--color-text-soft); font-size: var(--text-sm); }
.ra__list { list-style: none; margin: 0; padding: 0; }
.ra__row { display: flex; flex-direction: column; gap: 2px; padding: 10px var(--space-4); border-top: 1px solid var(--color-border); color: var(--color-text); text-decoration: none; }
.ra__row:hover { background: var(--color-surface-2); }
.ra__row--past { opacity: .7; }
.ra__title { font-size: var(--text-sm); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ra__meta { font-size: var(--text-xs); color: var(--color-text-soft); }
.ra__more { display: block; padding: 10px var(--space-4); border-top: 1px solid var(--color-border); font-size: var(--text-sm); color: var(--color-primary); text-decoration: none; }
</style>
